/**
 * Folder Dashboard Prototype + Dashboard Widget Builder
 * - Folder mode: widget/shortcode system for folder README.md dashboards
 * - Home Dashboard mode: editable widget grid with sidebar (add/remove/reorder/configure)
 *
 * Access: http://flownotes.localhost/?test=folder-dashboard
 * Reference: docs/product/bets/2026-01-28-folder-dashboard-system.md (Phase 4)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  FolderOpen,
  ExternalLink,
  CheckCircle2,
  Circle,
  Clock,
  User,
  AlertTriangle,
  Play,
  Pause,
  Settings,
  Code,
  Eye,
  LayoutGrid,
  List,
  ChevronDown,
  ChevronRight,
  Bot,
  Zap,
  Calendar,
  GripVertical,
  X,
  ChevronLeft,
  Plus,
  PanelRightClose,
  PanelRightOpen,
  FileText,
  Bookmark,
  Lightbulb,
  LayoutDashboard,
  Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SecondaryPanelContent } from '@/components/layout/secondary-panel-content';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
  type DraggableAttributes,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

// =============================================================================
// MOCK DATA - Based on real GGIA project folder
// =============================================================================

const mockFolder = {
  path: 'clients/greater-good/ggia/todos/8819316965-practice-category-navigation-updates',
  name: 'Practice Category & Navigation Updates',
  extensions: {
    basecamp: {
      enabled: true,
      todolist_id: '8819316965',
      project_name: 'GGIA',
      url: 'https://app.basecamp.com/5744549/buckets/39860713/todolists/8819316965'
    }
  }
};

const mockDocuments = [
  { id: '1', title: 'Global: Content updates (2h)', status: 'active', priority: 'high', assignee: 'Rowan Sentesy', estimate: '2h', type: 'todo', modified: '2026-01-29' },
  { id: '2', title: 'Global: Testing & QA (3-4h)', status: 'active', priority: 'high', assignee: 'Rowan Sentesy', estimate: '3-4h', type: 'qa', modified: '2026-01-15', overdue: true },
  { id: '3', title: 'Push changes to production', status: 'active', priority: 'medium', assignee: 'Rowan Sentesy', estimate: '1h', type: 'deploy', modified: '2026-01-28' },
  { id: '4', title: 'CMS Changes: create new Category Group', status: 'active', priority: 'medium', assignee: null, estimate: '1-2h', type: 'cms', modified: '2026-01-20' },
  { id: '5', title: 'CMS Changes: Rename existing Practice Categories', status: 'active', priority: 'low', assignee: null, estimate: '1h', type: 'cms', modified: '2026-01-20' },
  { id: '6', title: 'Generate weekly time report', status: 'active', priority: 'low', assignee: 'Rowan Sentesy', estimate: '30m', type: 'admin', modified: '2026-01-28' },
  { id: '7', title: 'Create estimate', status: 'completed', priority: null, assignee: 'Rowan Sentesy', estimate: null, type: 'planning', modified: '2025-10-15' },
  { id: '8', title: 'Analytics data', status: 'completed', priority: null, assignee: 'Rowan Sentesy', estimate: null, type: 'research', modified: '2025-10-20' },
  { id: '9', title: 'Navigation mockups', status: 'completed', priority: null, assignee: 'Kenneth King', estimate: '5h', type: 'design', modified: '2025-11-01' },
  { id: '10', title: 'Navigation HTML/CSS/JS', status: 'completed', priority: null, assignee: 'Rowan Sentesy', estimate: '2-3.5h', type: 'dev', modified: '2025-11-15' },
  { id: '11', title: 'Home page mockups', status: 'completed', priority: null, assignee: 'Kenneth King', estimate: '5h', type: 'design', modified: '2025-11-20' },
  { id: '12', title: 'Home page functionality', status: 'completed', priority: null, assignee: 'Rowan Sentesy', estimate: '2-4.5h', type: 'dev', modified: '2025-12-01' },
  { id: '13', title: 'Prepare handover', status: 'completed', priority: null, assignee: 'Kenneth King', estimate: null, type: 'handoff', modified: '2025-12-10' },
  { id: '14', title: 'Send spreadsheet', status: 'completed', priority: null, assignee: 'Rowan Sentesy', estimate: null, type: 'admin', modified: '2026-01-10' },
  { id: '15', title: 'Homepage Bug Analysis', status: 'completed', priority: null, assignee: 'Rowan Sentesy', estimate: null, type: 'debug', modified: '2025-12-15' },
];

const mockFlows = [
  {
    id: 'daily-briefing',
    name: 'Daily Briefing',
    schedule: '8:00am daily',
    status: 'active',
    lastRun: '2026-01-29 08:00',
    nextRun: '2026-01-30 08:00',
    output: 'Today: 3 Basecamp updates, 2 file changes. No blockers. Testing & QA still overdue.'
  },
  {
    id: 'basecamp-sync',
    name: 'Basecamp Sync',
    schedule: 'Every 15 min',
    status: 'active',
    lastRun: '2026-01-29 19:15',
    nextRun: '2026-01-29 19:30',
    output: 'Synced 15 todos, 0 new comments'
  },
  {
    id: 'weekly-report',
    name: 'Weekly Time Report',
    schedule: 'Fridays 5pm',
    status: 'paused',
    lastRun: '2026-01-24 17:00',
    nextRun: 'Paused',
    output: null
  },
];

// =============================================================================
// DASHBOARD WIDGET BUILDER — Types & registry
// =============================================================================

export type WidgetCategory = 'basecamp' | 'content' | 'ai' | 'general';

export type WidgetColSpan = 4 | 6 | 12;

export interface DashboardWidget {
  id: string;
  type: string;
  config: Record<string, unknown>;
  colSpan?: WidgetColSpan;
  /** Optional card/section heading (overrides default widget label). */
  title?: string;
  /** Optional subtitle or tooltip under the title. */
  description?: string;
}

export interface WidgetDefinition {
  type: string;
  label: string;
  description: string;
  icon: LucideIcon;
  defaultConfig: Record<string, unknown>;
  component: React.FC<{ config: Record<string, unknown> }>;
  configForm: React.FC<{
    config: Record<string, unknown>;
    onChange: (config: Record<string, unknown>) => void;
  }>;
  category: WidgetCategory;
}

/** Segment in an enhanced markdown dashboard: markdown block or widget. */
export type DashboardSegment =
  | { type: 'markdown'; id: string; content: string }
  | { type: 'widget'; widget: DashboardWidget };

// =============================================================================
// WIDGET COMPONENTS (shared by both folder shortcodes and builder)
// =============================================================================

// Basecamp Progress Widget
function BasecampProgressWidget({ todolistId }: { todolistId: string }) {
  const completed = mockDocuments.filter(d => d.status === 'completed').length;
  const total = mockDocuments.length;
  const percent = Math.round((completed / total) * 100);

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-orange-500/10 flex items-center justify-center">
            <ExternalLink className="w-4 h-4 text-orange-500" />
          </div>
          <div>
            <h3 className="font-medium text-sm">Basecamp Progress</h3>
            <p className="text-xs text-muted-foreground">Todolist #{todolistId}</p>
          </div>
        </div>
        <a
          href={mockFolder.extensions.basecamp.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline"
        >
          Open in Basecamp →
        </a>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>{completed} of {total} todos complete</span>
          <span className="font-medium">{percent}%</span>
        </div>
        <Progress value={percent} className="h-2" />
      </div>
    </div>
  );
}

// Board Widget
function BoardWidget({ view, documents }: { view: string; documents: typeof mockDocuments }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ archived: true });

  const columns = view === 'by-status'
    ? [
        { id: 'active', title: 'Active', color: 'blue', filter: (d: typeof mockDocuments[0]) => d.status === 'active' },
        { id: 'completed', title: 'Completed', color: 'green', filter: (d: typeof mockDocuments[0]) => d.status === 'completed' },
      ]
    : [
        { id: 'now', title: 'NOW', color: 'green', filter: (d: typeof mockDocuments[0]) => d.priority === 'high' },
        { id: 'next', title: 'NEXT', color: 'blue', filter: (d: typeof mockDocuments[0]) => d.priority === 'medium' },
        { id: 'later', title: 'LATER', color: 'gray', filter: (d: typeof mockDocuments[0]) => d.priority === 'low' || !d.priority },
      ];

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <LayoutGrid className="w-4 h-4" />
          Board View: {view === 'by-status' ? 'By Status' : 'Now/Next/Later'}
        </h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
            <Settings className="w-3 h-3 mr-1" />
            Configure
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {columns.map(column => {
          const items = documents.filter(column.filter);
          const isCollapsed = collapsed[column.id];

          return (
            <div key={column.id} className="space-y-2">
              <button
                className="flex items-center gap-2 w-full text-left"
                onClick={() => setCollapsed(prev => ({ ...prev, [column.id]: !prev[column.id] }))}
              >
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <span className={`text-sm font-medium text-${column.color}-500`}>
                  {column.title}
                </span>
                <span className="text-xs text-muted-foreground">({items.length})</span>
              </button>

              {!isCollapsed && (
                <div className="space-y-1 pl-6">
                  {items.map(doc => (
                    <DocumentCard key={doc.id} document={doc} compact />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Document Card Component
function DocumentCard({ document, compact = false }: { document: typeof mockDocuments[0]; compact?: boolean }) {
  const statusIcon = document.status === 'completed'
    ? <CheckCircle2 className="w-4 h-4 text-green-500" />
    : <Circle className="w-4 h-4 text-muted-foreground" />;

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer text-sm">
        {statusIcon}
        <span className={document.status === 'completed' ? 'text-muted-foreground line-through' : ''}>
          {document.title}
        </span>
        {document.overdue && (
          <AlertTriangle className="w-3 h-3 text-orange-500" />
        )}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg p-3 hover:border-primary/50 transition-colors bg-card">
      <div className="flex items-start gap-3">
        {statusIcon}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">{document.title}</h4>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {document.priority && (
              <span className={`px-1.5 py-0.5 text-xs rounded ${
                document.priority === 'high' ? 'bg-red-500/10 text-red-500' :
                document.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                'bg-gray-500/10 text-gray-500'
              }`}>
                {document.priority}
              </span>
            )}
            {document.type && (
              <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/10 text-blue-500">
                {document.type}
              </span>
            )}
            {document.overdue && (
              <span className="px-1.5 py-0.5 text-xs rounded bg-orange-500/10 text-orange-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                overdue
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            {document.assignee && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {document.assignee}
              </span>
            )}
            {document.estimate && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {document.estimate}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// File List Widget
function FileListWidget({ documents, filter, sort, limit }: {
  documents: typeof mockDocuments;
  filter?: string;
  sort?: string;
  limit?: number;
}) {
  let filtered = [...documents];

  if (filter === 'status:active') {
    filtered = filtered.filter(d => d.status === 'active');
  } else if (filter === 'status:overdue') {
    filtered = filtered.filter(d => d.overdue);
  }

  if (sort === 'modified') {
    filtered.sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime());
  }

  if (limit) {
    filtered = filtered.slice(0, limit);
  }

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <List className="w-4 h-4" />
          {filter === 'status:overdue' ? 'Overdue Items' : 'Recent Files'}
        </h3>
        <span className="text-xs text-muted-foreground">{filtered.length} items</span>
      </div>
      <div className="space-y-2">
        {filtered.map(doc => (
          <DocumentCard key={doc.id} document={doc} />
        ))}
      </div>
    </div>
  );
}

// AI Flow Widget
function AIFlowWidget({ flows }: { flows: typeof mockFlows }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Bot className="w-4 h-4" />
          AI Flows
        </h3>
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
          <Zap className="w-3 h-3 mr-1" />
          New Flow
        </Button>
      </div>
      <div className="space-y-3">
        {flows.map(flow => (
          <div key={flow.id} className="border border-border rounded p-3 bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {flow.status === 'active' ? (
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                )}
                <span className="font-medium text-sm">{flow.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  {flow.status === 'active' ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                </Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <Settings className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {flow.schedule}
              </span>
              <span>Last: {flow.lastRun}</span>
            </div>
            {flow.output && (
              <div className="mt-2 p-2 rounded bg-background text-xs text-muted-foreground">
                {flow.output}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Home-style widgets (config-driven, mock data) -----
function WelcomeHeaderWidget({ config }: { config: Record<string, unknown> }) {
  const userName = (config.userName as string) || 'there';
  const docsToday = (config.documentsEditedToday as number) ?? 3;
  const todosToday = (config.todosCompletedToday as number) ?? 1;
  return (
    <div className="border-b border-border pb-4">
      <h2 className="text-xl font-bold mb-1">Welcome back{userName !== 'there' ? `, ${userName}` : ''}!</h2>
      <p className="text-muted-foreground text-sm mb-3">Your intelligent project management workspace</p>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <span>{docsToday} document{docsToday !== 1 ? 's' : ''} edited today</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          <span>{todosToday} todo{todosToday !== 1 ? 's' : ''} completed</span>
        </div>
      </div>
    </div>
  );
}

function RecentActivityWidget({ config }: { config: Record<string, unknown> }) {
  const limit = (config.limit as number) ?? 5;
  const items = mockDocuments.slice(0, limit);
  return (
    <div className="space-y-2">
      <h3 className="font-medium text-sm mb-2">Recent Activity</h3>
      {items.map(doc => (
        <DocumentCard key={doc.id} document={doc} compact />
      ))}
    </div>
  );
}

function BasecampBookmarksWidget({ config }: { config: Record<string, unknown> }) {
  const limit = (config.limit as number) ?? 5;
  const overdue = mockDocuments.filter(d => d.overdue).slice(0, 2);
  const dueSoon = mockDocuments.filter(d => d.status === 'active' && !d.overdue).slice(0, limit - overdue.length);
  return (
    <div className="space-y-3">
      <h3 className="font-medium text-sm flex items-center gap-2">
        <Bookmark className="w-4 h-4" />
        Basecamp Bookmarks
      </h3>
      <div className="space-y-2">
        {overdue.length > 0 && (
          <div>
            <p className="text-xs text-orange-500 font-medium mb-1">Overdue</p>
            {overdue.map(d => (
              <DocumentCard key={d.id} document={d} compact />
            ))}
          </div>
        )}
        {dueSoon.map(d => (
          <DocumentCard key={d.id} document={d} compact />
        ))}
      </div>
    </div>
  );
}

function SmartSuggestionsWidget({ config }: { config: Record<string, unknown> }) {
  const limit = (config.limit as number) ?? 3;
  const suggestions = [
    { title: 'Testing & QA overdue', reason: 'Due 2 days ago' },
    { title: 'Unfiled voice memo', reason: 'Ready to organize' },
  ].slice(0, limit);
  return (
    <div className="space-y-2">
      <h3 className="font-medium text-sm flex items-center gap-2">
        <Lightbulb className="w-4 h-4" />
        Smart Suggestions
      </h3>
      <ul className="space-y-2 text-sm">
        {suggestions.map((s, i) => (
          <li key={i} className="flex items-center justify-between p-2 rounded bg-muted/50">
            <span>{s.title}</span>
            <span className="text-xs text-muted-foreground">{s.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickActionsWidget() {
  const actions = [
    { icon: Plus, label: 'New Document' },
    { icon: List, label: 'Search' },
    { icon: FolderOpen, label: 'Explorer' },
    { icon: LayoutGrid, label: 'Graph View' },
  ];
  return (
    <div className="space-y-2">
      <h3 className="font-medium text-sm mb-2">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-2">
        {actions.map(a => {
          const Icon = a.icon;
          return (
            <Button key={a.label} variant="outline" size="sm" className="justify-start h-9">
              <Icon className="w-4 h-4 mr-2" />
              {a.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

// Config-driven wrappers for existing widgets
function BasecampProgressWidgetByConfig({ config }: { config: Record<string, unknown> }) {
  const todolistId = (config.todolistId as string) || '8819316965';
  return <BasecampProgressWidget todolistId={todolistId} />;
}
function BoardWidgetByConfig({ config }: { config: Record<string, unknown> }) {
  const view = (config.view as string) || 'by-status';
  return <BoardWidget view={view} documents={mockDocuments} />;
}
function FileListWidgetByConfig({ config }: { config: Record<string, unknown> }) {
  const filter = (config.filter as string) || 'status:active';
  const sort = (config.sort as string) || 'modified';
  const limit = (config.limit as number) || 5;
  return <FileListWidget documents={mockDocuments} filter={filter} sort={sort} limit={limit} />;
}
function AIFlowWidgetByConfig() {
  return <AIFlowWidget flows={mockFlows} />;
}

// ----- Widget registry -----
const WIDGET_DEFINITIONS: WidgetDefinition[] = [
  {
    type: 'welcome-header',
    label: 'Welcome Header',
    description: 'Greeting and daily stats',
    icon: Home,
    category: 'general',
    defaultConfig: { userName: 'there', documentsEditedToday: 3, todosCompletedToday: 1 },
    component: WelcomeHeaderWidget,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>User name</Label>
          <Input
            value={(config.userName as string) || ''}
            onChange={e => onChange({ ...config, userName: e.target.value })}
            placeholder="there"
          />
        </div>
        <div className="space-y-2">
          <Label>Documents edited today (mock)</Label>
          <Input
            type="number"
            value={String(config.documentsEditedToday ?? 3)}
            onChange={e => onChange({ ...config, documentsEditedToday: parseInt(e.target.value, 10) || 0 })}
          />
        </div>
      </div>
    ),
  },
  {
    type: 'recent-activity',
    label: 'Recent Activity',
    description: 'Recently viewed or edited documents',
    icon: FileText,
    category: 'content',
    defaultConfig: { limit: 5 },
    component: RecentActivityWidget,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>Limit</Label>
          <Input
            type="number"
            value={String(config.limit ?? 5)}
            onChange={e => onChange({ ...config, limit: parseInt(e.target.value, 10) || 5 })}
          />
        </div>
      </div>
    ),
  },
  {
    type: 'basecamp-bookmarks',
    label: 'Basecamp Bookmarks',
    description: 'Overdue and due-soon todos',
    icon: Bookmark,
    category: 'basecamp',
    defaultConfig: { limit: 5 },
    component: BasecampBookmarksWidget,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>Limit</Label>
          <Input
            type="number"
            value={String(config.limit ?? 5)}
            onChange={e => onChange({ ...config, limit: parseInt(e.target.value, 10) || 5 })}
          />
        </div>
      </div>
    ),
  },
  {
    type: 'smart-suggestions',
    label: 'Smart Suggestions',
    description: 'Heuristic suggestions',
    icon: Lightbulb,
    category: 'ai',
    defaultConfig: { limit: 3 },
    component: SmartSuggestionsWidget,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>Limit</Label>
          <Input
            type="number"
            value={String(config.limit ?? 3)}
            onChange={e => onChange({ ...config, limit: parseInt(e.target.value, 10) || 3 })}
          />
        </div>
      </div>
    ),
  },
  {
    type: 'quick-actions',
    label: 'Quick Actions',
    description: 'New doc, search, explorer, graph',
    icon: Zap,
    category: 'general',
    defaultConfig: {},
    component: QuickActionsWidget,
    configForm: () => <p className="text-xs text-muted-foreground p-2">No configuration.</p>,
  },
  {
    type: 'basecamp-progress',
    label: 'Basecamp Progress',
    description: 'Todolist progress bar',
    icon: ExternalLink,
    category: 'basecamp',
    defaultConfig: { todolistId: '8819316965' },
    component: BasecampProgressWidgetByConfig,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>Todolist ID</Label>
          <Input
            value={(config.todolistId as string) || ''}
            onChange={e => onChange({ ...config, todolistId: e.target.value })}
            placeholder="8819316965"
          />
        </div>
      </div>
    ),
  },
  {
    type: 'board',
    label: 'Board View',
    description: 'Kanban by status or now/next/later',
    icon: LayoutGrid,
    category: 'content',
    defaultConfig: { view: 'by-status' },
    component: BoardWidgetByConfig,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>View</Label>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            value={(config.view as string) || 'by-status'}
            onChange={e => onChange({ ...config, view: e.target.value })}
          >
            <option value="by-status">By Status</option>
            <option value="now-next-later">Now / Next / Later</option>
          </select>
        </div>
      </div>
    ),
  },
  {
    type: 'file-list',
    label: 'File List',
    description: 'Filtered document list',
    icon: List,
    category: 'content',
    defaultConfig: { filter: 'status:active', sort: 'modified', limit: 5 },
    component: FileListWidgetByConfig,
    configForm: ({ config, onChange }) => (
      <div className="space-y-4 p-2">
        <div className="space-y-2">
          <Label>Filter</Label>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            value={(config.filter as string) || 'status:active'}
            onChange={e => onChange({ ...config, filter: e.target.value })}
          >
            <option value="status:active">Active</option>
            <option value="status:overdue">Overdue</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label>Limit</Label>
          <Input
            type="number"
            value={String(config.limit ?? 5)}
            onChange={e => onChange({ ...config, limit: parseInt(e.target.value, 10) || 5 })}
          />
        </div>
      </div>
    ),
  },
  {
    type: 'ai-flows',
    label: 'AI Flows',
    description: 'Scheduled AI automations',
    icon: Bot,
    category: 'ai',
    defaultConfig: {},
    component: AIFlowWidgetByConfig,
    configForm: () => <p className="text-xs text-muted-foreground p-2">No configuration.</p>,
  },
];

function getWidgetDefinition(type: string): WidgetDefinition | undefined {
  return WIDGET_DEFINITIONS.find(d => d.type === type);
}

// ----- Widget wrapper (drag handle, select, remove) -----
function WidgetWrapper({
  widget,
  isSelected,
  onSelect,
  onRemove,
  dragHandleListeners,
  dragHandleAttributes,
  isDragging,
  children,
}: {
  widget: DashboardWidget;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes: DraggableAttributes;
  isDragging: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'group relative rounded-lg border bg-card min-h-[80px] h-full flex flex-col',
        isDragging && 'opacity-50 z-50',
        isSelected && 'ring-2 ring-primary border-primary'
      )}
      onClick={e => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <div
        className={cn(
          'absolute left-2 top-2 z-10 cursor-grab active:cursor-grabbing rounded p-1 hover:bg-muted',
          isDragging && 'cursor-grabbing'
        )}
        {...dragHandleAttributes}
        {...dragHandleListeners}
        onClick={e => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-7 w-7 opacity-0 group-hover:opacity-100 z-10 transition-opacity"
        onClick={e => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <X className="w-4 h-4" />
      </Button>
      <div className="pt-10 pb-3 px-3 flex-1 min-h-0 overflow-auto flex flex-col">
        {(widget.title !== undefined && widget.title !== '') || (widget.description !== undefined && widget.description !== '') ? (
          <div className="flex-shrink-0 mb-2">
            <h3 className="text-sm font-medium text-foreground">
              {widget.title || getWidgetDefinition(widget.type)?.label || widget.type}
            </h3>
            {widget.description ? (
              <p className="text-xs text-muted-foreground mt-0.5">{widget.description}</p>
            ) : null}
          </div>
        ) : null}
        <div className="flex-1 min-h-0 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

function SortableWidgetCell({
  widget,
  isSelected,
  onSelect,
  onRemove,
  renderWidget,
}: {
  widget: DashboardWidget;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  renderWidget: (w: DashboardWidget) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const span = widget.colSpan ?? 6;
  const spanClass = span === 4 ? 'col-span-4' : span === 6 ? 'col-span-6' : 'col-span-12';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'min-h-0 relative',
        spanClass,
        isOver && 'ring-2 ring-primary ring-dashed rounded-lg bg-primary/5 min-h-[100px]'
      )}
    >
      {isOver && (
        <span className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10 z-10 text-sm font-medium text-primary pointer-events-none">
          Drop here
        </span>
      )}
      <div className={cn('h-full', isOver && 'opacity-40 pointer-events-none')}>
        <WidgetWrapper
          widget={widget}
          isSelected={isSelected}
          onSelect={onSelect}
          onRemove={onRemove}
          dragHandleListeners={listeners as Record<string, unknown>}
          dragHandleAttributes={attributes as unknown as DraggableAttributes}
          isDragging={isDragging}
        >
          {renderWidget(widget)}
        </WidgetWrapper>
      </div>
    </div>
  );
}

function WidgetGrid({
  widgets,
  selectedId,
  onSelect,
  onRemove,
  onReorder,
  renderWidget,
}: {
  widgets: DashboardWidget[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRemove: (id: string) => void;
  onReorder: (reordered: DashboardWidget[]) => void;
  renderWidget: (w: DashboardWidget) => React.ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = widgets.findIndex(w => w.id === active.id);
      const newIndex = widgets.findIndex(w => w.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(arrayMove(widgets, oldIndex, newIndex));
    },
    [widgets, onReorder]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={widgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
        <div
          className="grid grid-cols-12 gap-4 auto-rows-min"
          style={{ gridAutoFlow: 'dense' } as React.CSSProperties}
        >
          {widgets.map(w => (
            <SortableWidgetCell
              key={w.id}
              widget={w}
              isSelected={selectedId === w.id}
              onSelect={() => onSelect(w.id)}
              onRemove={() => onRemove(w.id)}
              renderWidget={renderWidget}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// ----- Widget sidebar (catalog vs config) -----
const CATEGORY_LABELS: Record<WidgetCategory, string> = {
  basecamp: 'Basecamp',
  content: 'Content',
  ai: 'AI',
  general: 'General',
};

function WidgetSidebar({
  selectedWidget,
  widgets,
  onSelectWidget,
  onAddWidget,
  onRemoveWidget,
  onConfigChange,
  onClose,
}: {
  selectedWidget: DashboardWidget | null;
  widgets: DashboardWidget[];
  onSelectWidget: (id: string | null) => void;
  onAddWidget: (type: string) => void;
  onRemoveWidget: (id: string) => void;
  onConfigChange: (
    id: string,
    config: Record<string, unknown>,
    options?: { colSpan?: WidgetColSpan; title?: string; description?: string }
  ) => void;
  onClose: () => void;
}) {
  const def = selectedWidget ? getWidgetDefinition(selectedWidget.type) : null;

  if (!selectedWidget) {
    return (
      <SecondaryPanelContent title="Add widget" onClose={onClose}>
        <div className="p-4 space-y-6">
          <p className="text-sm text-muted-foreground">Click a widget to add it to the dashboard.</p>
          {(Object.keys(CATEGORY_LABELS) as WidgetCategory[]).map(cat => (
            <div key={cat}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {CATEGORY_LABELS[cat]}
              </h3>
              <div className="space-y-1">
                {WIDGET_DEFINITIONS.filter(d => d.category === cat).map(d => {
                  const Icon = d.icon;
                  return (
                    <button
                      key={d.type}
                      type="button"
                      className="flex items-center gap-3 w-full p-3 rounded-lg border border-border bg-card hover:bg-muted/50 text-left"
                      onClick={() => onAddWidget(d.type)}
                    >
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{d.label}</div>
                        <div className="text-xs text-muted-foreground truncate">{d.description}</div>
                      </div>
                      <Plus className="w-4 h-4 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SecondaryPanelContent>
    );
  }

  const colSpan = selectedWidget.colSpan ?? 6;
  const COL_OPTIONS: WidgetColSpan[] = [4, 6, 12];

  return (
    <SecondaryPanelContent
      title={selectedWidget.title || def?.label || selectedWidget.type}
      breadcrumbs={[{ label: 'Widgets', onClick: () => onSelectWidget(null) }]}
      onClose={onClose}
      headerActions={
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => {
            onRemoveWidget(selectedWidget.id);
            onSelectWidget(null);
          }}
        >
          Remove
        </Button>
      }
    >
      <div className="p-2 space-y-4">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            value={selectedWidget.title ?? ''}
            onChange={e => onConfigChange(selectedWidget.id, selectedWidget.config, { title: e.target.value })}
            placeholder={def?.label ?? 'Widget title'}
          />
          <p className="text-xs text-muted-foreground">Optional card heading. Leave empty to use default widget label.</p>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={selectedWidget.description ?? ''}
            onChange={e => onConfigChange(selectedWidget.id, selectedWidget.config, { description: e.target.value })}
            placeholder="Optional subtitle"
          />
        </div>
        <div className="space-y-2">
          <Label>Columns (width)</Label>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            value={colSpan}
            onChange={e =>
              onConfigChange(selectedWidget.id, selectedWidget.config, {
                colSpan: parseInt(e.target.value, 10) as WidgetColSpan,
              })
            }
          >
            {COL_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n} of 12
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">Width of this widget in the 12-column grid.</p>
        </div>
        {def?.configForm && (
          <def.configForm
            config={selectedWidget.config}
            onChange={c => onConfigChange(selectedWidget.id, c)}
          />
        )}
      </div>
    </SecondaryPanelContent>
  );
}

// ----- Shortcode format (single source of truth for code view)
// Pattern: flat list of shortcodes, order = grid order. Each widget has:
//   - type (shortcode name), config attrs (limit, etc.), columns (4|6|12 = width in 12-col grid).
// No row/container needed: CSS grid auto-flow wraps by span. Same idea as Gridstack (flat list + layout
// props) and Grafana (data-driven panels); we express it as markdown shortcodes for README/dashboard edit.
// -----

/** Serialize dashboard to enhanced markdown (preamble + shortcodes + postamble). */
function serializeDashboardToCode(
  widgets: DashboardWidget[],
  preamble: string = '',
  postamble: string = ''
): string {
  const defaultPreamble = '# Home Dashboard\n\n<!-- Order = grid order; columns = width (4, 6, or 12 of 12). -->\n\n';
  const p = preamble !== undefined && preamble !== '' ? preamble : defaultPreamble;
  const lines: string[] = [];
  for (const w of widgets) {
    const name = w.type;
    const parts: string[] = [];
    const colSpan = w.colSpan ?? 6;
    parts.push(`columns="${colSpan}"`);
    if (w.title) parts.push(`title="${String(w.title).replace(/"/g, '\\"')}"`);
    if (w.description) parts.push(`description="${String(w.description).replace(/"/g, '\\"')}"`);
    if (w.config && Object.keys(w.config).length > 0) {
      for (const [k, v] of Object.entries(w.config)) {
        if (v !== undefined && v !== null && v !== '') {
          parts.push(`${k}="${String(v).replace(/"/g, '\\"')}"`);
        }
      }
    }
    lines.push(`{{${name} ${parts.join(' ')}}}`);
  }
  const shortcodeBlock = lines.join('\n');
  const out: string[] = [];
  if (p.trim()) out.push(p.trimEnd());
  if (shortcodeBlock) out.push(shortcodeBlock);
  if (postamble.trim()) out.push(postamble.trimStart());
  return out.join('\n\n');
}

/** Legacy: serialize widgets only (no preamble/postamble). */
function serializeWidgetsToShortcodes(widgets: DashboardWidget[]): string {
  return serializeDashboardToCode(widgets, '', '');
}

/** Parse full code view into preamble, widgets, and postamble. Preserves markdown before/after shortcodes. */
function parseCodeToDashboard(
  code: string
): { preamble: string; widgets: DashboardWidget[]; postamble: string } {
  const shortcodeRegex = /\{\{\s*([a-z0-9-]+)(?:\s+([^}]*))?\}\}/g;
  let match;
  const shortcodeRanges: { start: number; end: number }[] = [];
  while ((match = shortcodeRegex.exec(code)) !== null) {
    shortcodeRanges.push({ start: match.index, end: match.index + match[0].length });
  }
  const shortcodeBlock =
    shortcodeRanges.length > 0
      ? code.slice(shortcodeRanges[0].start, shortcodeRanges[shortcodeRanges.length - 1].end)
      : '';
  const preamble = shortcodeRanges.length > 0 ? code.slice(0, shortcodeRanges[0].start).trimEnd() : code.trimEnd();
  const postamble =
    shortcodeRanges.length > 0 ? code.slice(shortcodeRanges[shortcodeRanges.length - 1].end).trimStart() : '';
  const widgets = parseShortcodesToWidgets(shortcodeBlock);
  return { preamble, widgets, postamble };
}

/** Parse shortcode block (e.g. from code view) into DashboardWidget[]. Supports columns, title, description, and widget config. */
function parseShortcodesToWidgets(shortcodeBlock: string): DashboardWidget[] {
  const widgets: DashboardWidget[] = [];
  const regex = /\{\{\s*([a-z0-9-]+)(?:\s+([^}]*))?\}\}/g;
  let match;
  let index = 0;
  while ((match = regex.exec(shortcodeBlock)) !== null) {
    const type = match[1].trim();
    const attrString = (match[2] ?? '').trim();
    const config: Record<string, unknown> = {};
    let colSpan: WidgetColSpan = 6;
    let title: string | undefined;
    let description: string | undefined;
    const attrRegex = /(\w+)="([^"]*)"/g;
    let attrMatch;
    while ((attrMatch = attrRegex.exec(attrString)) !== null) {
      const key = attrMatch[1];
      const value = attrMatch[2];
      if (key === 'columns') {
        const n = parseInt(value, 10);
        if (n === 4 || n === 6 || n === 12) colSpan = n;
      } else if (key === 'title') {
        title = value || undefined;
      } else if (key === 'description') {
        description = value || undefined;
      } else {
        config[key] = /^\d+$/.test(value) ? parseInt(value, 10) : value;
      }
    }
    widgets.push({
      id: `parsed-${index}-${Date.now()}`,
      type,
      config,
      colSpan,
      title,
      description,
    });
    index += 1;
  }
  return widgets;
}

/** Parse full code into ordered segments (markdown + widget). */
function parseCodeToSegments(code: string): DashboardSegment[] {
  const shortcodeRegex = /\{\{\s*([a-z0-9-]+)(?:\s+([^}]*))?\}\}/g;
  const segments: DashboardSegment[] = [];
  let match;
  const matches: { start: number; end: number; full: string }[] = [];
  while ((match = shortcodeRegex.exec(code)) !== null) {
    matches.push({ start: match.index, end: match.index + match[0].length, full: match[0] });
  }
  let mdIndex = 0;
  if (matches.length === 0) {
    const trimmed = code.trim();
    if (trimmed) segments.push({ type: 'markdown', id: 'md-0', content: trimmed });
    return segments;
  }
  if (matches[0].start > 0) {
    const content = code.slice(0, matches[0].start).trimEnd();
    if (content) segments.push({ type: 'markdown', id: `md-${mdIndex++}`, content });
  }
  for (let i = 0; i < matches.length; i++) {
    const ws = parseShortcodesToWidgets(matches[i].full);
    if (ws[0]) segments.push({ type: 'widget', widget: ws[0] });
    const nextStart = i + 1 < matches.length ? matches[i + 1].start : code.length;
    if (nextStart > matches[i].end) {
      const content = code.slice(matches[i].end, nextStart).trim();
      if (content) segments.push({ type: 'markdown', id: `md-${mdIndex++}`, content });
    }
  }
  if (matches.length > 0 && matches[matches.length - 1].end < code.length) {
    const content = code.slice(matches[matches.length - 1].end).trimStart();
    if (content) segments.push({ type: 'markdown', id: `md-${mdIndex++}`, content });
  }
  return segments;
}

/** Serialize segments to enhanced markdown (markdown + shortcode lines). */
function serializeSegmentsToCode(segments: DashboardSegment[]): string {
  const lines: string[] = [];
  for (const seg of segments) {
    if (seg.type === 'markdown') {
      lines.push(seg.content);
    } else {
      const w = seg.widget;
      const name = w.type;
      const parts: string[] = [];
      const colSpan = w.colSpan ?? 6;
      parts.push(`columns="${colSpan}"`);
      if (w.title) parts.push(`title="${String(w.title).replace(/"/g, '\\"')}"`);
      if (w.description) parts.push(`description="${String(w.description).replace(/"/g, '\\"')}"`);
      if (w.config && Object.keys(w.config).length > 0) {
        for (const [k, v] of Object.entries(w.config)) {
          if (v !== undefined && v !== null && v !== '') {
            parts.push(`${k}="${String(v).replace(/"/g, '\\"')}"`);
          }
        }
      }
      lines.push(`{{${name} ${parts.join(' ')}}}`);
    }
  }
  return lines.join('\n\n');
}

// ----- View-only grid (no drag, no handles, no sidebar) -----
function ViewOnlyGrid({
  widgets,
  renderWidget,
}: {
  widgets: DashboardWidget[];
  renderWidget: (w: DashboardWidget) => React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-12 gap-4 auto-rows-min" style={{ gridAutoFlow: 'dense' } as React.CSSProperties}>
      {widgets.map(w => {
        const span = w.colSpan ?? 6;
        const spanClass = span === 4 ? 'col-span-4' : span === 6 ? 'col-span-6' : 'col-span-12';
        return (
          <div key={w.id} className={cn('min-h-0', spanClass)}>
            <div className="h-full rounded-lg border border-border bg-card p-4 overflow-auto">
              {renderWidget(w)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getSegmentId(seg: DashboardSegment): string {
  return seg.type === 'widget' ? seg.widget.id : seg.id;
}

function SortableSegmentRow({
  segment,
  selectedId,
  onSelect,
  onRemove,
  renderWidget,
  listLayout,
}: {
  segment: DashboardSegment;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRemove: (id: string) => void;
  renderWidget: (w: DashboardWidget) => React.ReactNode;
  /** When true, render as full-width list row (edit mode) so sortable strategy matches DOM. */
  listLayout?: boolean;
}) {
  const id = getSegmentId(segment);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({ id });

  const style = { transform: CSS.Transform.toString(transform), transition };

  if (segment.type === 'markdown') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        onClick={e => e.stopPropagation()}
        className={cn(
          'flex gap-2 items-start rounded-lg border border-border bg-card p-4 min-h-[60px] relative',
          listLayout ? 'w-full' : 'col-span-12',
          isDragging && 'opacity-0 pointer-events-none',
          isOver && 'ring-2 ring-primary ring-dashed rounded-lg bg-primary/5 border-primary/30'
        )}
      >
        {isOver && (
          <span className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10 z-10 text-sm font-medium text-primary pointer-events-none rounded-lg">
            Drop here
          </span>
        )}
        <button
          type="button"
          className={cn('mt-1 p-1 rounded cursor-grab active:cursor-grabbing text-muted-foreground hover:bg-muted', isOver && 'opacity-40')}
          {...listeners}
          {...attributes}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className={cn('flex-1 prose prose-sm dark:prose-invert max-w-none min-w-0', isOver && 'pointer-events-none')}>
          <ReactMarkdown>{segment.content}</ReactMarkdown>
        </div>
      </div>
    );
  }
  const w = segment.widget;
  const isSelected = selectedId === w.id;
  const span = w.colSpan ?? 6;
  const spanClass = listLayout ? 'w-full' : (span === 4 ? 'col-span-4' : span === 6 ? 'col-span-6' : 'col-span-12');
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={e => e.stopPropagation()}
      className={cn(
        'min-h-0 relative',
        spanClass,
        isOver && 'ring-2 ring-primary ring-dashed rounded-lg bg-primary/5 min-h-[80px]'
      )}
    >
      {isOver && (
        <span className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10 z-10 text-sm font-medium text-primary pointer-events-none">
          Drop here
        </span>
      )}
      {listLayout && (
        <span className="absolute right-3 top-3 z-10 rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {span} of 12 cols
        </span>
      )}
      <div
        className={cn(
          'h-full relative',
          isDragging && 'opacity-0 pointer-events-none',
          isOver && 'pointer-events-none opacity-60'
        )}
      >
        <WidgetWrapper
          widget={w}
          isSelected={isSelected}
          onSelect={() => onSelect(w.id)}
          onRemove={() => onRemove(w.id)}
          dragHandleListeners={listeners as Record<string, unknown>}
          dragHandleAttributes={attributes as unknown as DraggableAttributes}
          isDragging={isDragging}
        >
          {renderWidget(w)}
        </WidgetWrapper>
      </div>
    </div>
  );
}

// ----- Segment-based view (markdown + widgets in one grid) -----
function SegmentView({
  segments,
  renderWidget,
  editMode,
  selectedId,
  onSelect,
  onRemove,
  onReorderSegments,
}: {
  segments: DashboardSegment[];
  renderWidget: (w: DashboardWidget) => React.ReactNode;
  editMode: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRemove: (id: string) => void;
  onReorderSegments: (reordered: DashboardSegment[]) => void;
}) {
  const segmentIds = segments.map(getSegmentId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeSegment = activeId ? (segments.find(seg => getSegmentId(seg) === activeId) ?? null) : null;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || active.id === over.id) return;
      const oldIndex = segmentIds.indexOf(active.id as string);
      const newIndex = segmentIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorderSegments(arrayMove(segments, oldIndex, newIndex));
    },
    [segments, segmentIds, onReorderSegments]
  );
  // Use pointer position so dropzone is the row under the cursor. rectIntersection uses the
  // transformed source node (one row off when dragging). pointerWithin fails because the
  // pointer is over the DragOverlay. So: if we have pointer coords, find droppable that
  // contains the point; otherwise fall back to rectIntersection.
  const collisionDetection = useCallback(
    (args: {
      active: { id: string };
      collisionRect: { left: number; top: number; width: number; height: number };
      droppableRects: Map<string, { left: number; top: number; width: number; height: number }>;
      droppableContainers: { id: string }[];
      pointerCoordinates: { x: number; y: number } | null;
    }) => {
      const { pointerCoordinates, droppableRects, droppableContainers } = args;
      if (pointerCoordinates) {
        const { x, y } = pointerCoordinates;
        for (const container of droppableContainers) {
          const rect = droppableRects.get(container.id as string);
          if (!rect) continue;
          const { left, top, width, height } = rect;
          if (x >= left && x <= left + width && y >= top && y <= top + height) {
            return [{ id: container.id }];
          }
        }
      }
      return rectIntersection(args as Parameters<typeof rectIntersection>[0]) ?? closestCenter(args as Parameters<typeof closestCenter>[0]);
    },
    []
  );

  if (editMode) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection as CollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={segmentIds} strategy={verticalListSortingStrategy}>
          {/* Single-column list in edit mode so verticalListSortingStrategy matches DOM = predictable drag */}
          <div className="flex flex-col gap-4">
            {segments.map(seg => (
              <SortableSegmentRow
                key={getSegmentId(seg)}
                segment={seg}
                selectedId={selectedId}
                onSelect={onSelect}
                onRemove={onRemove}
                renderWidget={renderWidget}
                listLayout
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeSegment ? (
            <div className="rounded-lg border-2 border-primary bg-card shadow-lg p-4 min-w-[200px] max-w-[320px] flex items-center gap-3 cursor-grabbing">
              <GripVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              {activeSegment.type === 'markdown' ? (
                <span className="text-sm font-medium truncate">Markdown</span>
              ) : (
                <span className="text-sm font-medium truncate">
                  {activeSegment.widget.title || getWidgetDefinition(activeSegment.widget.type)?.label || activeSegment.widget.type}
                </span>
              )}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }
  return (
    <div className="grid grid-cols-12 gap-4 auto-rows-min" style={{ gridAutoFlow: 'dense' } as React.CSSProperties}>
      {segments.map(seg => {
        if (seg.type === 'markdown') {
          return (
            <div key={seg.id} className="col-span-12">
              <div className="prose prose-sm dark:prose-invert max-w-none py-2">
                <ReactMarkdown>{seg.content}</ReactMarkdown>
              </div>
            </div>
          );
        }
        const w = seg.widget;
        const span = w.colSpan ?? 6;
        const spanClass = span === 4 ? 'col-span-4' : span === 6 ? 'col-span-6' : 'col-span-12';
        return (
          <div key={w.id} className={cn('min-h-0', spanClass)}>
            <div className="h-full rounded-lg border border-border bg-card p-4 overflow-auto">
              {renderWidget(w)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ----- Home Dashboard Builder page -----
const DEFAULT_HOME_WIDGETS: DashboardWidget[] = [
  { id: 'w1', type: 'welcome-header', config: {}, colSpan: 12 },
  { id: 'w2', type: 'recent-activity', config: { limit: 5 }, colSpan: 6 },
  { id: 'w3', type: 'basecamp-bookmarks', config: { limit: 5 }, colSpan: 6 },
  { id: 'w4', type: 'smart-suggestions', config: { limit: 3 }, colSpan: 6 },
  { id: 'w5', type: 'quick-actions', config: {}, colSpan: 6 },
];

const DEFAULT_SEGMENTS: DashboardSegment[] = DEFAULT_HOME_WIDGETS.map(w => ({ type: 'widget', widget: w }));

const UNDO_MAX = 50;

function HomeDashboardBuilder() {
  const [segments, setSegments] = useState<DashboardSegment[]>(() => [...DEFAULT_SEGMENTS]);
  const segmentsRef = useRef<DashboardSegment[]>(segments);
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);
  const [segmentsHistory, setSegmentsHistory] = useState<DashboardSegment[][]>([]);
  const widgets = segments.filter((s): s is { type: 'widget'; widget: DashboardWidget } => s.type === 'widget').map(s => s.widget);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<'rendered' | 'code'>('rendered');

  const pushUndo = useCallback(() => {
    setSegmentsHistory(h => [...h.slice(-(UNDO_MAX - 1)), segmentsRef.current]);
  }, []);

  useEffect(() => {
    if (!editMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        setSegmentsHistory(h => {
          if (h.length === 0) return h;
          const rest = h.slice(0, -1);
          setSegments(h[h.length - 1]);
          return rest;
        });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editMode]);

  const addWidget = useCallback((type: string) => {
    const def = getWidgetDefinition(type);
    if (!def) return;
    const id = `w-${Date.now()}`;
    setSegments(prev => [...prev, { type: 'widget', widget: { id, type, config: { ...def.defaultConfig }, colSpan: 6 } }]);
    setSelectedId(null);
  }, []);

  const removeWidget = useCallback((id: string) => {
    pushUndo();
    setSegments(prev => prev.filter(s => s.type !== 'widget' || s.widget.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId, pushUndo]);

  const updateConfig = useCallback(
    (
      id: string,
      config: Record<string, unknown>,
      options?: { colSpan?: WidgetColSpan; title?: string; description?: string }
    ) => {
      setSegments(prev =>
        prev.map(s =>
          s.type === 'widget' && s.widget.id === id
            ? {
                type: 'widget' as const,
                widget: {
                  ...s.widget,
                  config,
                  ...(options?.colSpan !== undefined && { colSpan: options.colSpan }),
                  ...(options?.title !== undefined && { title: options.title || undefined }),
                  ...(options?.description !== undefined && { description: options.description || undefined }),
                },
              }
            : s
        )
      );
    },
    []
  );

  const renderWidget = useCallback((w: DashboardWidget) => {
    const def = getWidgetDefinition(w.type);
    if (!def) return <div className="text-sm text-muted-foreground">Unknown: {w.type}</div>;
    const C = def.component;
    return <C config={w.config} />;
  }, []);

  const [codeText, setCodeText] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const showCodeView = editMode && viewMode === 'code';
  const showRenderedView = !showCodeView;

  const applyCodeToDashboard = useCallback(() => {
    setCodeError(null);
    const parsed = parseCodeToSegments(codeText);
    const widgetSegments = parsed.filter((s): s is { type: 'widget'; widget: DashboardWidget } => s.type === 'widget');
    if (widgetSegments.length === 0) {
      setCodeError('No valid shortcodes found. Use {{widget-type key="value"}} format.');
      return;
    }
    const known = new Set(WIDGET_DEFINITIONS.map(d => d.type));
    const unknown = widgetSegments.filter(s => !known.has(s.widget.type));
    if (unknown.length > 0) {
      setCodeError(`Unknown widget type(s): ${unknown.map(s => s.widget.type).join(', ')}`);
      return;
    }
    pushUndo();
    setSegments(parsed);
    setViewMode('rendered');
  }, [codeText, pushUndo]);

  useEffect(() => {
    if (viewMode === 'code') {
      setCodeText(serializeSegmentsToCode(segments));
      setCodeError(null);
    }
  }, [viewMode, segments]);

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="border-b p-4 bg-sidebar flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <LayoutDashboard className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-lg font-bold">Home Dashboard</h1>
          {editMode && (
            <div className="flex items-center gap-1">
              <Button
                variant={viewMode === 'rendered' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('rendered')}
                className="h-7 px-2"
              >
                <Eye className="w-4 h-4 mr-1" />
                Rendered
              </Button>
              <Button
                variant={viewMode === 'code' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('code')}
                className="h-7 px-2"
              >
                <Code className="w-4 h-4 mr-1" />
                Code
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
                Done
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(prev => !prev)}
              >
                {sidebarOpen ? <PanelRightClose className="w-4 h-4 mr-1" /> : <PanelRightOpen className="w-4 h-4 mr-1" />}
                {sidebarOpen ? 'Close sidebar' : 'Edit widgets'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setEditMode(true)}>
              Edit widgets
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        <div
          className={cn('flex-1 overflow-auto p-6 min-w-0', editMode && sidebarOpen && 'border-r border-border')}
          onClick={() => editMode && setSelectedId(null)}
        >
          <div className="max-w-5xl mx-auto">
            {showCodeView && (
              <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-3">
                <p className="text-xs text-muted-foreground">
                  Edit shortcodes below. Order = grid order; <code className="text-foreground">columns</code> = width (4, 6, or 12). Apply to update the dashboard.
                </p>
                <textarea
                  className="w-full min-h-[240px] text-sm font-mono p-4 rounded border border-input bg-background text-foreground resize-y"
                  value={codeText}
                  onChange={e => setCodeText(e.target.value)}
                  spellCheck={false}
                />
                {codeError && (
                  <p className="text-sm text-destructive">{codeError}</p>
                )}
                <Button onClick={applyCodeToDashboard}>
                  Apply to dashboard
                </Button>
              </div>
            )}
            {showRenderedView && (
              <SegmentView
                segments={segments}
                renderWidget={renderWidget}
                editMode={editMode}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onRemove={removeWidget}
                onReorderSegments={newSegments => {
                  pushUndo();
                  setSegments(newSegments);
                }}
              />
            )}
          </div>
        </div>

        {editMode && sidebarOpen && (
          <div className="w-80 flex-shrink-0 border-l border-border flex flex-col bg-card">
            <WidgetSidebar
              selectedWidget={widgets.find(w => w.id === selectedId) ?? null}
              widgets={widgets}
              onSelectWidget={setSelectedId}
              onAddWidget={addWidget}
              onRemoveWidget={removeWidget}
              onConfigChange={updateConfig}
              onClose={() => setSidebarOpen(false)}
            />
          </div>
        )}
      </div>

      <div className="border-t p-3 bg-muted/30 text-xs text-muted-foreground flex-shrink-0">
        <div className="max-w-5xl mx-auto">
          {editMode
            ? 'Drag to reorder • Click widget to configure • Add from sidebar • Code view shows shortcodes • ⌘Z to undo'
            : 'Use the dashboard — click “Edit widgets” to add, remove, or reorder.'}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SHORTCODE PARSER (Mock)
// =============================================================================

function parseShortcodes(markdown: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const regex = /\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(markdown)) !== null) {
    // Add text before the shortcode
    if (match.index > lastIndex) {
      nodes.push(<span key={key++}>{markdown.slice(lastIndex, match.index)}</span>);
    }

    // Parse shortcode
    const shortcode = match[1].trim();
    const [widget, ...params] = shortcode.split(/\s+/);
    const props: Record<string, string> = {};
    params.forEach(param => {
      const [key, value] = param.split('=');
      if (key && value) {
        props[key] = value.replace(/"/g, '');
      }
    });

    // Render widget
    switch (widget) {
      case 'basecamp-progress':
        nodes.push(<BasecampProgressWidget key={key++} todolistId={props.todolist || '8819316965'} />);
        break;
      case 'board':
        nodes.push(<BoardWidget key={key++} view={props.view || 'by-status'} documents={mockDocuments} />);
        break;
      case 'file-list':
        nodes.push(<FileListWidget key={key++} documents={mockDocuments} filter={props.filter} sort={props.sort} limit={props.limit ? parseInt(props.limit) : undefined} />);
        break;
      case 'ai-flows':
        nodes.push(<AIFlowWidget key={key++} flows={mockFlows} />);
        break;
      default:
        nodes.push(
          <div key={key++} className="border border-dashed border-yellow-500/50 rounded p-2 text-xs text-yellow-500 bg-yellow-500/5">
            Unknown widget: {widget}
          </div>
        );
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < markdown.length) {
    nodes.push(<span key={key++}>{markdown.slice(lastIndex)}</span>);
  }

  return nodes;
}

// =============================================================================
// MAIN COMPONENT (mode: Home Dashboard | Folder Dashboard)
// =============================================================================

function FolderDashboardContent() {
  const [viewMode, setViewMode] = useState<'rendered' | 'code'>('rendered');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Mock README.md content with shortcodes
  const readmeWithShortcodes = `# Practice Category & Navigation Updates

**Basecamp Todolist:** [8819316965](https://app.basecamp.com/5744549/buckets/39860713/todolists/8819316965)
**Project:** GGIA
**Total Estimate:** 45 hours

## Project Status

{{basecamp-progress todolist="8819316965"}}

## Work Overview

{{board view="by-status"}}

## Overdue Items

{{file-list filter="status:overdue" sort="modified"}}

## AI Automation

{{ai-flows}}

## Recent Activity

{{file-list filter="status:active" sort="modified" limit="5"}}
`;

  return (
    <div className="h-full flex flex-col bg-background min-h-0">
      {/* Header */}
      <div className="border-b p-4 bg-sidebar flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold">{mockFolder.name}</h1>
                <span className="px-2 py-0.5 text-xs rounded bg-orange-500/10 text-orange-500 border border-orange-500/20">
                  Basecamp Project
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {mockFolder.path}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={mockFolder.extensions.basecamp.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Basecamp
            </a>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b px-4 bg-sidebar">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between">
            <TabsList className="bg-transparent border-none h-10">
              <TabsTrigger value="dashboard" className="data-[state=active]:bg-background">
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="board" className="data-[state=active]:bg-background">
                Board
              </TabsTrigger>
              <TabsTrigger value="list" className="data-[state=active]:bg-background">
                List
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-background">
                Settings
              </TabsTrigger>
            </TabsList>

            {activeTab === 'dashboard' && (
              <div className="flex items-center gap-1">
                <Button
                  variant={viewMode === 'rendered' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('rendered')}
                  className="h-7 px-2"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Rendered
                </Button>
                <Button
                  variant={viewMode === 'code' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('code')}
                  className="h-7 px-2"
                >
                  <Code className="w-4 h-4 mr-1" />
                  Code
                </Button>
              </div>
            )}
          </div>
        </Tabs>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsContent value="dashboard" className="mt-0">
              {viewMode === 'code' ? (
                <div className="border border-border rounded-lg p-4 bg-muted/30">
                  <pre className="text-sm font-mono whitespace-pre-wrap text-muted-foreground">
                    {readmeWithShortcodes}
                  </pre>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Parse and render shortcodes */}
                  {readmeWithShortcodes.split('\n').map((line, i) => {
                    if (line.startsWith('{{')) {
                      return <div key={i}>{parseShortcodes(line)}</div>;
                    } else if (line.startsWith('# ')) {
                      return <h1 key={i} className="text-2xl font-bold mt-6 mb-2">{line.slice(2)}</h1>;
                    } else if (line.startsWith('## ')) {
                      return <h2 key={i} className="text-lg font-semibold mt-6 mb-3">{line.slice(3)}</h2>;
                    } else if (line.startsWith('**')) {
                      return <p key={i} className="text-sm text-muted-foreground">{line}</p>;
                    } else if (line.trim()) {
                      return <p key={i} className="text-sm">{line}</p>;
                    }
                    return null;
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="board" className="mt-0">
              <BoardWidget view="by-status" documents={mockDocuments} />
            </TabsContent>

            <TabsContent value="list" className="mt-0">
              <div className="space-y-2">
                {mockDocuments.map(doc => (
                  <DocumentCard key={doc.id} document={doc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="settings" className="mt-0">
              <div className="border border-border rounded-lg p-6 bg-card">
                <h2 className="text-lg font-semibold mb-4">Folder Settings</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium mb-2">Extensions</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 border border-border rounded">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-orange-500/10 flex items-center justify-center">
                            <ExternalLink className="w-4 h-4 text-orange-500" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Basecamp</p>
                            <p className="text-xs text-muted-foreground">Todolist #8819316965</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">Configure</Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium mb-2">Default View</h3>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm">Dashboard</Button>
                      <Button variant="outline" size="sm">Board</Button>
                      <Button variant="outline" size="sm">List</Button>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium mb-2">Board Template</h3>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm">By Status</Button>
                      <Button variant="outline" size="sm">Now/Next/Later</Button>
                      <Button variant="outline" size="sm">By Priority</Button>
                      <Button variant="outline" size="sm">Custom...</Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Info bar */}
      <div className="border-t p-3 bg-muted/30 text-xs text-muted-foreground">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <span>Prototype: Folder Dashboard System</span>
          <span>Test case: GGIA Practice Category project • {mockDocuments.length} documents • {mockFlows.length} AI flows</span>
        </div>
      </div>
    </div>
  );
}

/** Entry: Home Dashboard (widget builder) | Folder Dashboard (shortcode) */
export function FolderDashboardTest() {
  const [mode, setMode] = useState<'home' | 'folder'>('home');
  return (
    <div className="h-screen flex flex-col bg-background">
      <div className="border-b px-4 py-2 bg-sidebar flex items-center gap-4">
        <Tabs value={mode} onValueChange={v => setMode(v as 'home' | 'folder')}>
          <TabsList className="bg-transparent border-none h-10">
            <TabsTrigger value="home" className="data-[state=active]:bg-background">
              Home Dashboard
            </TabsTrigger>
            <TabsTrigger value="folder" className="data-[state=active]:bg-background">
              Folder Dashboard
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {mode === 'home' ? <HomeDashboardBuilder /> : <FolderDashboardContent />}
      </div>
    </div>
  );
}
